import { LightBulbIcon } from './icons';
import PerformanceTabGre from './performance_tab_gre';
import style from './style.module.scss';
import { Answer, RecommendationRequest, SubjectTagStat, TabEnum } from './types';
import AnswerResultIcon from '@magoosh/pages/answer_reviews/table/answer_result_icon';
import AnswerFlag from '@magoosh/organisms/answer_flag';
import { BorderlessButton, SecondaryButton } from '@magoosh/lib/button';
import { paths } from 'config/path_helpers';
import moment from 'moment';
import { get } from 'lodash';
import * as React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { secondsFormattedAsMinutes } from 'utilities/time';
import { toSentence } from 'utilities/formatting';
import TutorRecommendationsTabGre from './tutor_recommendations_tab_gre';

interface Props {
  estimates: Record<string, string>;
  lastActivityAt: number;
  mockTest: {
    createdAt: number;
    id: number;
    name: string;
    reasonScoreNotDisplayed: string;
    shouldDisplayScoresToUser: boolean;
  };
  /**
   * Subject tags, at least for GRE, are grouped as
   * { math: SubjectTagStat[], verbal: SubjectTagStat[] }
   *  */
  performanceBreakdownByExamSection: Record<string, SubjectTagStat[]>;
  recommendationRequest?: RecommendationRequest;
  resultingStudyPlanFocus?: {
    math: string[];
    verbal: string[];
  };
  sections: {
    displayName: string;
    isScored: boolean;
    answers: Answer[];
    percentCorrect: number;
  }[];
  ui: {
    shouldShowTutorRecommendations: boolean;
  };
}

export const PerformanceSummary: React.FC<Props> = (props) => {
  const [activeTab, setTab] = React.useState<TabEnum>('performance');
  const [isSubmitted, setSubmitted] = React.useState(!!get(props, 'recommendationRequest.id'));
  const handleFormComplete = () => setSubmitted(true);

  const renderActiveTab = () => {
    if (activeTab === 'performance') {
      return (
        <PerformanceTabGre
          lastActivityAt={props.lastActivityAt}
          examSections={props.performanceBreakdownByExamSection}
          onClickAnswers={() => setTab('answers')}
          onClickRecommendations={() => setTab('tutor_recommendations')}
          resultingStudyPlanFocus={props.resultingStudyPlanFocus}
          shouldShowRecommendationsInvitation={shouldShowRecommendationsInvitation()}
        />
      );
    } else if (activeTab === 'answers') {
      return <AnswersTab sections={props.sections} />;
    } else if (activeTab === 'tutor_recommendations') {
      return (
        <TutorRecommendationsTabGre
          {...props.recommendationRequest}
          mockTestId={props.mockTest.id}
          isSubmitted={isSubmitted}
          onComplete={handleFormComplete}
        />
      );
    }
  };

  const shouldShowRecommendationsInvitation = () => {
    if (isSubmitted) return false;
    if (get(props, 'recommendationRequest.isComplete')) return false;

    return props.ui.shouldShowTutorRecommendations;
  };

  const tabsToDisplay = () => {
    let tabs = [
      { name: 'Performance', slug: 'performance' },
      { name: 'Answers', slug: 'answers' }
    ];
    if (props.ui.shouldShowTutorRecommendations) {
      tabs.push({ name: 'Tutor Recommendations', slug: 'tutor_recommendations' });
    }
    return tabs;
  };

  return (
    <div className="u-margin-T-l">
      <div className={style.header}>
        <div className={style.title}>
          <span className={style.grayDarker} style={{ fontSize: '32px' }}>
            <strong>{props.mockTest.name}</strong>
          </span>
          <br />
          <span style={{ fontSize: '18px' }}>{moment(props.mockTest.createdAt * 1000).format('LL')}</span>
        </div>
        {props.mockTest.shouldDisplayScoresToUser ? (
          <div className={style.scoreContainerEstimated}>
            <div>
              <LightBulbIcon height={50} width={50} />
              <span className="text-primary" style={{ fontSize: '30px' }}>
                Your score
              </span>
            </div>
            <div>
              <span>Math</span>
              {props.estimates['Math'] ? (
                <span>{props.estimates['Math']}</span>
              ) : (
                <OverlayTrigger overlay={<Tooltip id="no-score">No section score available</Tooltip>}>
                  <span>--</span>
                </OverlayTrigger>
              )}
            </div>
            <div>
              <span>Verbal</span>
              {props.estimates['Verbal'] ? (
                <span>{props.estimates['Verbal']}</span>
              ) : (
                <OverlayTrigger overlay={<Tooltip id="no-score">No section score available</Tooltip>}>
                  <span>--</span>
                </OverlayTrigger>
              )}
            </div>
            <div>
              <span>Overall</span>
              {props.estimates['Overall'] ? (
                <span>{props.estimates['Overall']}</span>
              ) : (
                <OverlayTrigger overlay={<Tooltip id="no-score">No overall score available</Tooltip>}>
                  <span>--</span>
                </OverlayTrigger>
              )}
            </div>
          </div>
        ) : (
          <div className={style.scoreContainerNone}>
            <div>
              <LightBulbIcon height={50} width={50} />
              <span className="text-primary" style={{ fontSize: '30px' }}>
                Not Scored
              </span>
            </div>
            <div>
              {props.mockTest.reasonScoreNotDisplayed}
            </div>
          </div>
        )}
      </div>

      <hr />

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {tabsToDisplay().map((tab) => {
          if (activeTab === tab.slug) {
            return (
              <SecondaryButton className="u-margin-R-m" key={tab.slug}>
                {tab.name}
              </SecondaryButton>
            );
          } else {
            return (
              <BorderlessButton
                className="u-margin-R-m"
                key={tab.slug}
                onClick={() => setTab(tab.slug as TabEnum)}>
                {tab.name}
              </BorderlessButton>
            );
          }
        })}
      </div>

      <hr />

      {renderActiveTab()}
    </div>
  );
};

const AnswersTab = (props: { sections: Props['sections'] }) => {
  const formatSubjectTags = (tags) => {
    if (!tags.length) return null;

    // Some answers have multiple subject tags with a repeated portion. This is especially common
    // for verbal subjects tags where there could be tags like follows on a single prompt:
    // - Sentence Equivalence: Tough Vocabulary, Sentence Equivalence: Psuedo-synonyms
    // This function alters the tags so that the shared "Sentence Equivalence: " portion is only
    // displayed once at the beginning.
    if (tags[0].indexOf(': ') !== -1) {
      const sharedTagBucket =  tags[0].split(': ')[0] + ': ';
      const adjustedTags = tags.map((tag, idx) => {
        if (idx === 0) {
          return tag;
        } else {
          return tag.replace(sharedTagBucket, '');
        }
      });
      return toSentence(adjustedTags);
    } else {
      return toSentence(tags);
    }    
  };

  return (
    <div>
      {props.sections.map((section, idx) => {
        return (
          <div key={idx}>
            <h2>
              {section.displayName}
              {section.isScored && <div className="pull-right">{section.percentCorrect}% correct</div>}
            </h2>
            {section.answers.length == 0 ? (
              <em>You didn&apos;t answer any questions from this section.</em>
            ) : (
              <div className="table-responsive">
                <table className="table">
                  <tbody>
                    {section.answers.map((answer) => {
                      return (
                        <tr key={answer.id}>
                          <td style={{ width: '5%' }}>
                            <AnswerResultIcon {...answer} />
                          </td>
                          <td style={{ width: '30%' }}>
                            <a href={paths.answer(answer.id)} target="_blank" rel="noopener">
                              Question {answer.indexInSession}
                            </a>
                          </td>
                          <td style={{ width: '40%' }}>{formatSubjectTags(answer.subjectTags)}</td>
                          <td style={{ width: '10%' }}>{answer.humanDifficulty}</td>
                          <td style={{ width: '10%' }}>{secondsFormattedAsMinutes(answer.yourPace)}</td>
                          <td style={{ width: '5%' }}>
                            <AnswerFlag flagged={answer.isFlagged} promptId={answer.prompt.id} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PerformanceSummary;
